import React, { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Container,
  Link,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ArrowBack, Article } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { mediBlogService, MediBlogArticle } from "../services/mediBlog";
import { getLocalizedArticleField, getLocalizedCategoryName } from "../utils/mediBlogLocale";
import { useSeoMeta } from "../utils/seoMeta";

const MediBlogArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [article, setArticle] = useState<MediBlogArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      setLoading(true);
      const { data } = await mediBlogService.getArticleBySlug(slug, true);
      setArticle(data);
      setNotFound(!data);
      setLoading(false);
    };
    void load();
  }, [slug]);

  const title = article ? getLocalizedArticleField(article, "title", i18n.language) : "";
  const excerpt = article ? getLocalizedArticleField(article, "excerpt", i18n.language) : "";
  const content = article ? getLocalizedArticleField(article, "content", i18n.language) : "";

  useSeoMeta({
    title: article?.meta_title || (title ? `${title} | MediBlog` : t("mediblog.title")),
    description: article?.meta_description || excerpt || t("mediblog.seo.listDescription"),
    keywords: article?.meta_keywords || t("mediblog.seo.listKeywords"),
    ogTitle: article?.og_title || title,
    ogDescription: article?.og_description || excerpt,
    ogImage: article?.og_image || article?.featured_image,
    canonicalUrl: article?.canonical_url || (slug ? `${window.location.origin}/mediblog/${slug}` : undefined),
    noIndex: article?.no_index,
  });

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8, minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound || !article) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning">{t("mediblog.articleNotFound")}</Alert>
        <Link component={RouterLink} to="/mediblog" sx={{ display: "inline-flex", alignItems: "center", mt: 2, gap: 0.5 }}>
          <ArrowBack fontSize="small" />
          {t("mediblog.backToList")}
        </Link>
      </Container>
    );
  }

  const category = article.category;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/mediblog" underline="hover" color="inherit">
          {t("mediblog.title")}
        </Link>
        <Typography color="text.primary" noWrap sx={{ maxWidth: 280 }}>
          {title}
        </Typography>
      </Breadcrumbs>

      {category && (
        <Chip
          label={getLocalizedCategoryName(category, i18n.language)}
          size="small"
          sx={{ mb: 2 }}
        />
      )}

      <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: "1.75rem", md: "2.5rem" } }}>
        {title}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {article.author_name && `${article.author_name} · `}
        {formatDate(article.published_at)}
      </Typography>

      {article.featured_image ? (
        <Box
          component="img"
          src={article.featured_image}
          alt={title}
          sx={{
            width: "100%",
            maxHeight: 420,
            objectFit: "cover",
            borderRadius: 2,
            mb: 4,
          }}
        />
      ) : (
        <Box
          sx={{
            height: 200,
            borderRadius: 2,
            mb: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.06),
          }}
        >
          <Article sx={{ fontSize: 64, color: "primary.main", opacity: 0.4 }} />
        </Box>
      )}

      {excerpt && (
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mb: 4, fontStyle: "italic", lineHeight: 1.6 }}
        >
          {excerpt}
        </Typography>
      )}

      <Box
        component="article"
        sx={{
          "& p": { mb: 2, lineHeight: 1.8 },
          "& h2, & h3": { mt: 3, mb: 1.5, fontWeight: 600 },
          whiteSpace: "pre-wrap",
          fontSize: "1.05rem",
        }}
      >
        {content.split("\n\n").map((paragraph, i) => (
          <Typography key={i} paragraph>
            {paragraph}
          </Typography>
        ))}
      </Box>

      <Link
        component={RouterLink}
        to="/mediblog"
        sx={{ display: "inline-flex", alignItems: "center", mt: 4, gap: 0.5 }}
      >
        <ArrowBack fontSize="small" />
        {t("mediblog.backToList")}
      </Link>
    </Container>
  );
};

export default MediBlogArticlePage;
